import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ChangePassword: React.FC = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const { changePassword } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match.');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      setIsLoading(false);
      return;
    }

    // Validate current password is not empty
    if (!formData.currentPassword) {
      setError('Please enter your current password.');
      setIsLoading(false);
      return;
    }

    try {
      const success = await changePassword(formData.currentPassword, formData.newPassword);
      if (success) {
        setMessage('Password changed successfully! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError('Failed to change password. Please check your current password and try again.');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <div className="row justify-content-center">
      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title mb-0">Change Password</h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bx bx-error-circle me-2"></i>
                  {error}
                </div>
              )}

              {message && (
                <div className="alert alert-success" role="alert">
                  <i className="bx bx-check-circle me-2"></i>
                  {message}
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="currentPassword" className="form-label">
                  Current Password
                </label>
                <div className="input-group auth-pass-inputgroup">
                  <input
                    type={showPassword.current ? 'text' : 'password'}
                    className="form-control"
                    id="currentPassword"
                    name="currentPassword"
                    placeholder="Enter current password"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                  />
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    disabled={isLoading}
                  >
                    <i className={`mdi ${showPassword.current ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}`}></i>
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="newPassword" className="form-label">
                  New Password
                </label>
                <div className="input-group auth-pass-inputgroup">
                  <input
                    type={showPassword.new ? 'text' : 'password'}
                    className="form-control"
                    id="newPassword"
                    name="newPassword"
                    placeholder="Enter new password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                  />
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    disabled={isLoading}
                  >
                    <i className={`mdi ${showPassword.new ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}`}></i>
                  </button>
                </div>
                <small className="form-text text-muted">
                  Password must be at least 6 characters long.
                </small>
              </div>

              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm New Password
                </label>
                <div className="input-group auth-pass-inputgroup">
                  <input
                    type={showPassword.confirm ? 'text' : 'password'}
                    className="form-control"
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                  />
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    disabled={isLoading}
                  >
                    <i className={`mdi ${showPassword.confirm ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}`}></i>
                  </button>
                </div>
              </div>

              <div className="mt-4 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary waves-effect waves-light"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary waves-effect waves-light"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
